import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { User, Key, ShoppingBag, Copy, Check, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const StatsCard = ({ icon: Icon, label, value, iconColor = "text-[#4F7CFF]" }) => (
  <div className="bg-[#0a0a12] border border-white/5 p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 ${iconColor}`} />
      <span className="text-[#A1A1AA] uppercase text-sm tracking-wider">{label}</span>
    </div>
    <span className="text-3xl font-mono text-white">{value}</span>
  </div>
);

const OrderCard = ({ order, onCopy, copiedKey }) => {
  const now = new Date();
  const isExpired = new Date(order.expires_at) <= now;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const diff = new Date(expiresAt) - now;
    if (diff <= 0) return "Expired";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  return (
    <div
      className={`bg-[#0a0a12] border ${isExpired ? "border-white/5" : "border-[#4F7CFF]/30"} p-6`}
      data-testid={`order-${order.id}`}
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">{order.game}</p>
            <span className={`px-2 py-0.5 text-xs uppercase ${
              isExpired 
                ? "bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30" 
                : "bg-[#4F7CFF]/10 text-[#4F7CFF] border border-[#4F7CFF]/30"
            }`}>
              {order.duration}
            </span>
          </div>
          <h3 className="font-heading text-xl text-white mb-2">{order.product_name}</h3>
          <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
            <span>Purchased: {formatDate(order.created_at)}</span>
            <span className={isExpired ? "text-[#FF3B30]" : "text-[#4F7CFF]"}>
              {isExpired ? "Expired" : getTimeRemaining(order.expires_at)}
            </span>
          </div>
        </div>

        <div className="lg:text-right">
          <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">License Key</p>
          <div className="flex items-center gap-2">
            <code className={`license-key ${isExpired ? "opacity-50" : ""}`}>
              {order.license_key}
            </code>
            <button
              onClick={() => onCopy(order.license_key, order.id)}
              className="p-2 text-[#A1A1AA] hover:text-[#4F7CFF] transition-colors"
              data-testid={`copy-key-${order.id}`}
            >
              {copiedKey === order.id ? (
                <Check className="w-5 h-5 text-[#4F7CFF]" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-sm font-mono mt-2">
            <span className="text-[#A1A1AA]">Paid:</span>{" "}
            <span className="text-[#4F7CFF]">${order.price}</span>
          </p>
        </div>
      </div>

      {isExpired && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <a
            href={`/products/${order.product_id}`}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Renew License
          </a>
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);
  const [activeTab, setActiveTab] = useState("active");

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const copyToClipboard = async (key, orderId) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(orderId);
      toast.success("License key copied!");
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const now = new Date();
  const activeOrders = orders.filter(o => new Date(o.expires_at) > now && o.status === "completed");
  const expiredOrders = orders.filter(o => new Date(o.expires_at) <= now || o.status !== "completed");
  const displayOrders = activeTab === "active" ? activeOrders : expiredOrders;

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="dashboard-page">
      <Navbar />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-16 h-16 bg-[#4F7CFF]/20 flex items-center justify-center text-[#4F7CFF]">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl text-white">{user?.username}</h1>
              <p className="text-[#A1A1AA]">{user?.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <StatsCard icon={ShoppingBag} label="Total Orders" value={orders.length} />
            <StatsCard icon={Key} label="Active Keys" value={activeOrders.length} />
            <StatsCard icon={Clock} label="Expired Keys" value={expiredOrders.length} iconColor="text-[#A1A1AA]" />
          </div>

          <div className="flex gap-4 border-b border-white/10 mb-6">
            <button
              onClick={() => setActiveTab("active")}
              className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors ${
                activeTab === "active" ? "tab-active" : "tab-inactive"
              }`}
              data-testid="tab-active"
            >
              Active Keys ({activeOrders.length})
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`pb-4 px-2 text-sm uppercase tracking-wider transition-colors ${
                activeTab === "history" ? "tab-active" : "tab-inactive"
              }`}
              data-testid="tab-history"
            >
              History ({expiredOrders.length})
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#4F7CFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : displayOrders.length === 0 ? (
            <div className="text-center py-20 bg-[#0a0a12] border border-white/5">
              <ShoppingBag className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
              <p className="text-[#A1A1AA] mb-4">
                {activeTab === "active" 
                  ? "No active licenses. Purchase a product to get started!" 
                  : "No purchase history yet."}
              </p>
              {activeTab === "active" && (
                <a href="/products" className="btn-primary inline-block">
                  Browse Products
                </a>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {displayOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onCopy={copyToClipboard}
                  copiedKey={copiedKey}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DashboardPage;
